import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, RefreshCw, SearchX, TriangleAlert } from 'lucide-react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/ui/states';

/**
 * "No orders" and "the request failed" must never render the same.
 *
 * `aggregator_order_links` and `integration_event_deliveries` are both
 * tenant-scoped under `tenant_isolation` RLS, so an unbound read returns
 * **zero rows rather than an error** — the exact failure
 * `Pages/Integrations/components/PanelResult.tsx` names, kept local here for
 * the same reason that file gives: the copy is this screen's own. It matters
 * more on THIS screen than most: a floor role staring at an empty incoming
 * queue has no other signal that the feed is actually broken, and the row it
 * is missing is a marketplace order somebody is waiting to have cooked.
 */
export interface PanelResultProps {
  loading: boolean;
  error: string | null;
  count: number;
  onRetry: () => void;
  filtered?: boolean;
  onClearFilters?: () => void;
  emptyTitle: string;
  emptyHint?: string;
  emptyAction?: ReactNode;
  children: ReactNode;
}

export function PanelResult({
  loading,
  error,
  count,
  onRetry,
  filtered,
  onClearFilters,
  emptyTitle,
  emptyHint,
  emptyAction,
  children,
}: PanelResultProps) {
  const { t } = useTranslation();

  if (loading) return <LoadingState />;

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50/70 p-6 text-center dark:border-red-900 dark:bg-red-950/30"
      >
        <div className="grid place-items-center gap-2">
          <Icon as={TriangleAlert} size="xl" className="text-red-600 dark:text-red-400" />
          <p className="text-sm font-medium">{t('deliveryAggregators.states.loadFailed')}</p>
          <p className="max-w-lg text-sm text-ink-muted">{error}</p>
          <p className="max-w-lg text-xs text-ink-muted">{t('deliveryAggregators.states.loadFailedHint')}</p>
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
            <Icon as={RefreshCw} />
            {t('common.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  if (count === 0 && filtered) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={SearchX} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{t('deliveryAggregators.states.noMatches')}</p>
        <p className="max-w-sm text-sm text-ink-muted">{t('deliveryAggregators.states.noMatchesHint')}</p>
        {onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            {t('deliveryAggregators.states.clearFilters')}
          </Button>
        )}
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={Inbox} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{emptyTitle}</p>
        {emptyHint && <p className="max-w-sm text-sm text-ink-muted">{emptyHint}</p>}
        {emptyAction}
      </div>
    );
  }

  return <>{children}</>;
}

export default PanelResult;
